import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import styles from "./PriceDisplay.module.css";
import type { BetPhase } from "../store/game";
import { useGame } from "../store/game";
import { formatUsd } from "../lib/format";

type Props = {
  price: number | null;
  phase: BetPhase;
};

/**
 * Big rolling-digit BTC price (Polymarket-style). Also surfaces:
 *   - countdown chip while a bet is pending
 *   - +$/-$ chip on result reveal
 *   - flash burst behind the price when a bet is placed
 */
export function PriceDisplay({ price, phase }: Props) {
  const shareResult = useGame((s) => s.shareResult);
  const [flashSide, setFlashSide] = useState<"up" | "down" | null>(null);

  // Trigger a flash on every transition into "pending".
  useEffect(() => {
    if (phase.kind !== "pending") return;
    setFlashSide(phase.side);
    const t = setTimeout(() => setFlashSide(null), 650);
    return () => clearTimeout(t);
  }, [phase.kind, phase.kind === "pending" ? phase.side : null]);

  const tokens = useMemo(
    () => (price === null ? null : tokenizePrice(price)),
    [price],
  );

  return (
    <div className={styles.root}>
      <div className={styles.label}>
        <span className={styles.dot} />
        BTC / USD
      </div>

      <div className={styles.price}>
        {flashSide && (
          <span
            className={`${styles.flash} ${
              flashSide === "up" ? styles.flashUp : styles.flashDown
            } ${styles.flashActive}`}
          />
        )}
        {tokens === null ? (
          <span className={styles.fixed}>— — — —</span>
        ) : (
          tokens.map((tok, i) =>
            tok.kind === "digit" ? (
              <RollingDigit key={i} digit={tok.value} />
            ) : (
              <span key={i} className={styles.fixed}>
                {tok.value}
              </span>
            ),
          )
        )}
      </div>

      <div className={styles.statusRow}>
        {phase.kind === "pending" && (
          <span className={styles.countdown}>
            <span className={styles.countdownDot} />
            {phase.secondsLeft}s — bet locked at {formatUsd(phase.priceAtBet)}
          </span>
        )}
        {phase.kind === "result" && (
          <ResultChip
            outcome={phase.outcome}
            payoutDelta={phase.payoutDelta}
            tokensDelta={phase.tokensDelta}
            onShare={() => phase.kind === "result" && shareResult(phase.payoutDelta, phase.side)}
          />
        )}
      </div>
    </div>
  );
}

function ResultChip({
  outcome,
  payoutDelta,
  tokensDelta,
  onShare,
}: {
  outcome: "win" | "loss" | "draw";
  payoutDelta: number;
  tokensDelta: number;
  onShare: () => void;
}) {
  const klass =
    outcome === "win"
      ? styles.deltaWin
      : outcome === "loss"
        ? styles.deltaLoss
        : styles.deltaDraw;
  const sign = payoutDelta > 0 ? "+" : payoutDelta < 0 ? "−" : "±";
  const dollars = formatUsd(Math.abs(payoutDelta));
  return (
    <motion.div
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, pointerEvents: "auto" }}
      initial={{ y: 6, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <span className={klass}>
        {sign} {dollars}
        {tokensDelta > 0 && ` · +${tokensDelta} GT`}
      </span>
      <button type="button" className={styles.shareBtn} onClick={onShare}>
        ↗ Поділитись результатом
      </button>
    </motion.div>
  );
}

/* ── Digit roll ─────────────────────────────────────────────────────── */

type Token =
  | { kind: "digit"; value: number }
  | { kind: "fixed"; value: string };

function tokenizePrice(price: number): Token[] {
  const text = formatUsd(price);
  return Array.from(text).map((ch) => {
    const code = ch.charCodeAt(0);
    if (code >= 48 && code <= 57) {
      return { kind: "digit", value: code - 48 };
    }
    return { kind: "fixed", value: ch };
  });
}

function RollingDigit({ digit }: { digit: number }) {
  return (
    <span className={styles.digit}>
      <motion.span
        className={styles.digitTrack}
        animate={{ y: `-${digit}em` }}
        transition={{ type: "spring", stiffness: 260, damping: 28, mass: 0.7 }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <span key={d} className={styles.digitCell}>
            {d}
          </span>
        ))}
      </motion.span>
    </span>
  );
}
